import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  CHAT_MESSAGES_LOADING,
  CHAT_MESSAGES_SENDING,
  INSTAGRAM_CHAT_LOADING,
  WHATSAPP_CHAT_LOADING,
  LINKEDIN_CHAT_LOADING,
  LINKEDIN_BUSINESS_CHAT_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { useMeCore } from "../me";
import { socialChatApiService } from "./services/api";
import {
  setConversationSocialAction,
  setDateFilters,
  setSelectedFacebookConversationAction,
  setSelectedInstagramConversationAction,
  setSelectedLinkedinBusinessConversationAction,
  setSelectedLinkedinConversationAction,
  setSelectedWhatsappConversationAction,
  syncFacebookConversationWithContactAction as syncMetaConversationWithContactAction,
} from "./store/actions";
import {
  getFacebookConversationsFailureAction,
  getFacebookConversationsInitAction,
  selectConversationStatusToShow,
  getFacebookConversationsSuccessAction,
  getFacebookMessagesFailureAction,
  getFacebookMessagesInitAction,
  getFacebookMessagesSuccessAction,
  getInstagramConversationsFailureAction,
  getInstagramConversationsInitAction,
  getInstagramConversationsSuccessAction,
  getInstagramMessagesFailureAction,
  getInstagramMessagesInitAction,
  getInstagramMessagesSuccessAction,
  getWhatsappConversationsFailureAction,
  getWhatsappConversationsInitAction,
  getWhatsappConversationsSuccessAction,
  getWhatsappMessagesFailureAction,
  getWhatsappMessagesInitAction,
  getWhatsappMessagesSuccessAction,
  postFacebookMessageFailureAction,
  postFacebookMessageInitAction,
  postFacebookMessageSuccessAction,
  postFacebookSeenMessageFailureAction,
  postFacebookSeenMessageInitAction,
  postFacebookSeenMessageSuccessAction,
  postInstagramMessageFailureAction,
  postInstagramMessageInitAction,
  postInstagramMessageSuccessAction,
  postWhatsappMessageFailureAction,
  postWhatsappMessageInitAction,
  postWhatsappMessageSuccessAction,
  getLinkedinConversationsFailureAction,
  getLinkedinConversationsInitAction,
  getLinkedinConversationsSuccessAction,
  getLinkedinMessagesFailureAction,
  getLinkedinMessagesInitAction,
  getLinkedinMessagesSuccessAction,
  postLinkedinMessageFailureAction,
  postLinkedinMessageInitAction,
  postLinkedinMessageSuccessAction,
  getLinkedinBusinessConversationsFailureAction,
  getLinkedinBusinessConversationsInitAction,
  getLinkedinBusinessConversationsSuccessAction,
  getLinkedinBusinessMessagesFailureAction,
  getLinkedinBusinessMessagesInitAction,
  getLinkedinBusinessMessagesSuccessAction,
  postLinkedinBusinessMessageFailureAction,
  postLinkedinBusinessMessageInitAction,
  postLinkedinBusinessMessageSuccessAction,
} from "./store/api-actions";
import {
  dateFilterSelector,
  facebookConversationsSelector,
  facebookMessagesSelector,
  facebookSelectedSelector,
  instagramConversationsSelector,
  instagramMessagesSelector,
  instagramSelectedSelector,
  socialChatRoot,
  whatsappConversationsSelector,
  whatsappMessagesSelector,
  whatsappSelectedSelector,
  linkedinConversationsSelector,
  linkedinMessagesSelector,
  linkedinSelectedSelector,
  linkedinBusinessConversationsSelector,
  linkedinBusinessMessagesSelector,
  linkedinBusinessSelectedSelector,
} from "./store/selector";
import { Attachment } from "../conversations/interfaces/attachment/attachment.interface";
import { ContentMessageType } from "./types/whatsapp-message.types";
import { ConversationStatus } from "./types/conversation-status.types";
import { ContactStateRequest } from "../contacts/interfaces/contact-state-request";
import { SocialType } from "./types/social.types";
import { ConversationContact } from "./interfaces/conversations.interface";
import { FACEBOOK_CHAT_LOADING } from "../../constants/loading.constant";
import { buildOptimisticSocialMessage } from "./utils/optimistic-message";

export function useSocialChatCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { selectedSocial } = useSelector(socialChatRoot);
  const instagramConversations = useSelector(instagramConversationsSelector);
  const { conversationStatus } = useSelector(socialChatRoot);
  const dateFilter = useSelector(dateFilterSelector);
  const instagramSelected = useSelector(instagramSelectedSelector);
  const instagramMessages = useSelector(instagramMessagesSelector);
  const facebookConversations = useSelector(facebookConversationsSelector);
  const facebookSelected = useSelector(facebookSelectedSelector);
  const facebookMessages = useSelector(facebookMessagesSelector);
  const whatsappConversations = useSelector(whatsappConversationsSelector);
  const whatsappSelected = useSelector(whatsappSelectedSelector);
  const whatsappMessages = useSelector(whatsappMessagesSelector);
  const linkedinConversations = useSelector(linkedinConversationsSelector);
  const linkedinSelected = useSelector(linkedinSelectedSelector);
  const linkedinMessages = useSelector(linkedinMessagesSelector);
  const linkedinBusinessConversations = useSelector(
    linkedinBusinessConversationsSelector
  );
  const linkedinBusinessSelected = useSelector(linkedinBusinessSelectedSelector);
  const linkedinBusinessMessages = useSelector(linkedinBusinessMessagesSelector);

  const { turnOnLoading, turnOffLoading } = useGlobalCore();
  const { socialSettings } = useMeCore();

  const getInstagramConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getInstagramConversationsInitAction());
        turnOnLoading(INSTAGRAM_CHAT_LOADING);
        const response = await socialChatApiService.getInstagramConversations(
          companyId
        );
        dispatch(getInstagramConversationsSuccessAction(response));
        turnOffLoading(INSTAGRAM_CHAT_LOADING);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(INSTAGRAM_CHAT_LOADING);
        dispatch(getInstagramConversationsFailureAction({ error }));
        toast.error(
          intl.formatMessage({ id: "app.chat.instagram.loadConversationsFailed" }),
          { autoClose: 7000 }
        );
      }
    },
    [dispatch, turnOnLoading, turnOffLoading, intl]
  );
  const getInstagramMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getInstagramMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getInstagramMessages(
          companyId,
          conversationId
        );
        dispatch(getInstagramMessagesSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getInstagramMessagesFailureAction({ error }));
        toast.error(
          intl.formatMessage({ id: "app.chat.instagram.loadMessagesFailed" }),
          { autoClose: 7000 }
        );
      } finally {
        turnOffLoading(CHAT_MESSAGES_LOADING);
      }
    },
    [dispatch, intl, turnOnLoading, turnOffLoading]
  );

  const syncInstagramInboxSilent = useCallback(
    async (companyId: string, conversationId?: string | null) => {
      try {
        const response = await socialChatApiService.getInstagramConversations(
          companyId
        );
        dispatch(getInstagramConversationsSuccessAction(response));
        if (conversationId) {
          const messagesResponse =
            await socialChatApiService.getInstagramMessages(
              companyId,
              conversationId
            );
          dispatch(getInstagramMessagesSuccessAction(messagesResponse));
        }
      } catch (error: any) {
        console.warn("[Instagram inbox] silent sync failed:", error);
      }
    },
    [dispatch]
  );

  const postInstagramMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      message: string
    ) => {
      const instagramSetting =
        socialSettings?.instagramLogin || socialSettings?.instagramMensseger;
      const optimisticMessage = buildOptimisticSocialMessage({
        text: message,
        from: {
          id: instagramSetting?.organizationId || "",
          name: instagramSetting?.name || "",
          avatarUrl: instagramSetting?.avatarUrl,
        },
      });

      try {
        dispatch(
          postInstagramMessageInitAction({
            conversationId,
            message: optimisticMessage,
          })
        );
        const response = await socialChatApiService.postInstagramMessage(
          companyId,
          conversationId,
          recipientId,
          message as string
        );
        dispatch(postInstagramMessageSuccessAction(response));
        turnOffLoading(CHAT_MESSAGES_SENDING, 500);
      } catch (error: any) {
        turnOffLoading(CHAT_MESSAGES_SENDING);

        console.error(error);
        dispatch(postInstagramMessageFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, socialSettings]
  );
  const getFacebookConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getFacebookConversationsInitAction());
        turnOnLoading(FACEBOOK_CHAT_LOADING);
        const response = await socialChatApiService.getFacebookConversations(
          companyId
        );
        dispatch(getFacebookConversationsSuccessAction(response));
        turnOffLoading(FACEBOOK_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(FACEBOOK_CHAT_LOADING);
        dispatch(getFacebookConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getFacebookMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getFacebookMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getFacebookMessages(
          companyId,
          conversationId
        );
        dispatch(getFacebookMessagesSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getFacebookMessagesFailureAction({ error }));
      } finally {
        turnOffLoading(CHAT_MESSAGES_LOADING);
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postFacebookMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      content?: string
    ) => {
      const optimisticMessage = buildOptimisticSocialMessage({
        text: content as string,
        from: {
          id: socialSettings?.facebook?.userId || "",
          name: socialSettings?.facebook?.name || "",
          avatarUrl: socialSettings?.facebook?.avatarUrl,
        },
      });

      try {
        dispatch(
          postFacebookMessageInitAction({
            conversationId,
            message: optimisticMessage,
          })
        );
        const response = await socialChatApiService.postFacebookMessage(
          companyId,
          conversationId,
          recipientId,
          content as string
        );

        dispatch(
          postFacebookMessageSuccessAction({
            ...response,
          })
        );
        turnOffLoading(CHAT_MESSAGES_SENDING);
      } catch (error: any) {
        turnOffLoading(CHAT_MESSAGES_SENDING);
        console.error(error);
        dispatch(postFacebookMessageFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, socialSettings]
  );
  const postFacebookSeenMessage = useCallback(
    async (companyId: string, conversationId: string, recipientId: string) => {
      try {
        dispatch(postFacebookSeenMessageInitAction());
        const response = await socialChatApiService.postFacebookSeenMessage(
          companyId,
          conversationId,
          recipientId
        );
        dispatch(postFacebookSeenMessageSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(postFacebookSeenMessageFailureAction({ error }));
      }
    },
    [dispatch]
  );

  const getLinkedinConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getLinkedinConversationsInitAction());
        turnOnLoading(LINKEDIN_CHAT_LOADING);
        const response = await socialChatApiService.getLinkedinConversations(
          companyId
        );
        dispatch(getLinkedinConversationsSuccessAction(response));
        turnOffLoading(LINKEDIN_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(LINKEDIN_CHAT_LOADING);
        dispatch(getLinkedinConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getLinkedinMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getLinkedinMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getLinkedinMessages(
          companyId,
          conversationId
        );
        dispatch(getLinkedinMessagesSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getLinkedinMessagesFailureAction({ error }));
      } finally {
        turnOffLoading(CHAT_MESSAGES_LOADING);
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postLinkedinMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      content?: string
    ) => {
      try {
        dispatch(postLinkedinMessageInitAction());
        const response = await socialChatApiService.postLinkedinMessage(
          companyId,
          conversationId,
          recipientId,
          content as string
        );
        dispatch(postLinkedinMessageSuccessAction({ ...response }));
        turnOffLoading(CHAT_MESSAGES_SENDING);
      } catch (error: any) {
        turnOffLoading(CHAT_MESSAGES_SENDING);
        console.error(error);
        dispatch(postLinkedinMessageFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading]
  );

  const getLinkedinBusinessConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getLinkedinBusinessConversationsInitAction());
        turnOnLoading(LINKEDIN_BUSINESS_CHAT_LOADING);
        const response =
          await socialChatApiService.getLinkedinBusinessConversations(
            companyId
          );
        dispatch(getLinkedinBusinessConversationsSuccessAction(response));
        turnOffLoading(LINKEDIN_BUSINESS_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(LINKEDIN_BUSINESS_CHAT_LOADING);
        dispatch(getLinkedinBusinessConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getLinkedinBusinessMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getLinkedinBusinessMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getLinkedinBusinessMessages(
          companyId,
          conversationId
        );
        dispatch(getLinkedinBusinessMessagesSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getLinkedinBusinessMessagesFailureAction({ error }));
      } finally {
        turnOffLoading(CHAT_MESSAGES_LOADING);
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postLinkedinBusinessMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      content?: string
    ) => {
      try {
        dispatch(postLinkedinBusinessMessageInitAction());
        const response = await socialChatApiService.postLinkedinBusinessMessage(
          companyId,
          conversationId,
          recipientId,
          content as string
        );
        dispatch(postLinkedinBusinessMessageSuccessAction({ ...response }));
        turnOffLoading(CHAT_MESSAGES_SENDING);
      } catch (error: any) {
        turnOffLoading(CHAT_MESSAGES_SENDING);
        console.error(error);
        dispatch(postLinkedinBusinessMessageFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading]
  );

  const getWhatsappConversations = useCallback(
    async (companyId: string, contactStateRequest: ContactStateRequest) => {
      try {
        dispatch(getWhatsappConversationsInitAction());
        turnOnLoading(WHATSAPP_CHAT_LOADING);
        const response = await socialChatApiService.getWhatsappConversations(
          companyId,
          contactStateRequest
        );
        dispatch(getWhatsappConversationsSuccessAction(response));
        turnOffLoading(WHATSAPP_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(WHATSAPP_CHAT_LOADING);
        dispatch(getWhatsappConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );

  const getWhatsappMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getWhatsappMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getWhatsappMessages(
          companyId,
          conversationId
        );
        dispatch(getWhatsappMessagesSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getWhatsappMessagesFailureAction({ error }));
      } finally {
        turnOffLoading(CHAT_MESSAGES_LOADING);
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postWhatsappMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      message: string,
      type: ContentMessageType,
      attachments: Attachment[] = []
    ) => {
      try {
        dispatch(postWhatsappMessageInitAction());
        const response = await socialChatApiService.postWhatsappMessage({
          type,
          companyId,
          conversationId,
          recipientId,
          message,
          attachments,
        });
        dispatch(
          postWhatsappMessageSuccessAction({
            ...response,
          })
        );
        turnOffLoading(CHAT_MESSAGES_SENDING, 500);
      } catch (error: any) {
        turnOffLoading(CHAT_MESSAGES_SENDING, 500);
        console.error(error);
        dispatch(postWhatsappMessageFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading]
  );

  return {
    instagramConversations,
    instagramSelected,
    instagramMessages,
    facebookConversations,
    facebookSelected,
    facebookMessages,
    linkedinConversations,
    linkedinSelected,
    linkedinMessages,
    linkedinBusinessConversations,
    linkedinBusinessSelected,
    linkedinBusinessMessages,
    whatsappConversations,
    whatsappSelected,
    whatsappMessages,
    getInstagramConversations,
    getInstagramMessages,
    syncInstagramInboxSilent,
    postInstagramMessage,
    selectedSocial,
    setSelectedSocial: (social: SocialType) =>
      dispatch(setConversationSocialAction({ social })),
    selectInstagramConversation: (id: string | null) =>
      dispatch(
        setSelectedInstagramConversationAction({
          id,
        })
      ),
    getFacebookConversations,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    selectFacebookConversation: (id: string | null) =>
      dispatch(
        setSelectedFacebookConversationAction({
          id,
        })
      ),
    getLinkedinConversations,
    getLinkedinMessages,
    postLinkedinMessage,
    selectLinkedinConversation: (id: string | null) =>
      dispatch(
        setSelectedLinkedinConversationAction({
          id,
        })
      ),
    getLinkedinBusinessConversations,
    getLinkedinBusinessMessages,
    postLinkedinBusinessMessage,
    selectLinkedinBusinessConversation: (id: string | null) =>
      dispatch(
        setSelectedLinkedinBusinessConversationAction({
          id,
        })
      ),
    getWhatsappConversations,

    getWhatsappMessages,
    postWhatsappMessage,
    conversationStatus,
    updateConversationContact: (
      contact: ConversationContact,
      social: "facebook" | "instagram" | "linkedin" | "linkedinBusiness"
    ) => {
      turnOffLoading(INSTAGRAM_CHAT_LOADING);
      turnOffLoading(FACEBOOK_CHAT_LOADING);
      turnOffLoading(LINKEDIN_CHAT_LOADING);
      turnOffLoading(LINKEDIN_BUSINESS_CHAT_LOADING);

      dispatch(
        syncMetaConversationWithContactAction({
          contact,
          social,
        })
      );
    },
    setConversationStatus: (status: ConversationStatus) => {
      dispatch(selectConversationStatusToShow({ conversationStatus: status }));
    },
    selectWhatsappConversation: (id: string | null) =>
      dispatch(
        setSelectedWhatsappConversationAction({
          id,
        })
      ),
    setDateFilter: (date: string, dateToUpdate: "from" | "to") =>
      dispatch(setDateFilters({ date, dateToUpdate })),
    dateFilter,
  };
}
